import React from "react";
import { Box, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import RecommendIcon from "@mui/icons-material/Recommend";
import BiotechIcon from "@mui/icons-material/Biotech";

// Mobile-optimised version of CompactRangeBar (matches intro page design)
const PreviewRangeBar = ({ value, min, max, normalMin, normalMax, unit }) => {
  const totalRange = max - min;
  const normalStartPercent = ((normalMin - min) / totalRange) * 100;
  const normalWidthPercent = ((normalMax - normalMin) / totalRange) * 100;
  const valuePercent = Math.min(Math.max(((value - min) / totalRange) * 100, 2), 98);
  const isNormal = value >= normalMin && value <= normalMax;

  return (
    <Box sx={{ width: "100%", mt: { xs: 0.75, sm: 1 } }}>
      {/* Range Bar Container */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 34, sm: 38 },
          display: "flex",
          alignItems: "flex-end",
          mb: 0.5,
        }}
      >
        {/* The Bar */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 8, sm: 10 },
            borderRadius: 5,
            overflow: "visible",
          }}
        >
          {/* Low Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: "0%",
              width: `${normalStartPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #ef4444, #dc2626)",
              borderTopLeftRadius: 5,
              borderBottomLeftRadius: 5,
            }}
          />

          {/* Normal Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent}%`,
              width: `${normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #86efac, #4ade80)",
              boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
            }}
          />

          {/* High Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent + normalWidthPercent}%`,
              width: `${100 - normalStartPercent - normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #dc2626, #ef4444)",
              borderTopRightRadius: 5,
              borderBottomRightRadius: 5,
            }}
          />

          {/* Low Label - Left Side */}
          <Box
            sx={{
              position: "absolute",
              left: "0%",
              top: -15,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                background: "rgba(254, 226, 226, 0.85)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "1px solid #dc2626",
                color: "#dc2626",
                px: 0.5,
                py: 0.1,
                borderRadius: "4px",
                fontSize: "7px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(220, 38, 38, 0.2)",
                letterSpacing: "0.25px",
              }}
            >
              Low
            </Box>
          </Box>

          {/* High Label - Right Side */}
          <Box
            sx={{
              position: "absolute",
              right: "0%",
              top: -15,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                background: "rgba(254, 226, 226, 0.85)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "1px solid #dc2626",
                color: "#dc2626",
                px: 0.5,
                py: 0.1,
                borderRadius: "4px",
                fontSize: "7px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(220, 38, 38, 0.2)",
                letterSpacing: "0.25px",
              }}
            >
              High
            </Box>
          </Box>

          {/* Value Marker with bubble + pointer */}
          <Box
            sx={{
              position: "absolute",
              left: `${valuePercent}%`,
              bottom: "100%",
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Box
              sx={{
                background: isNormal
                  ? "rgba(220, 252, 231, 0.9)"
                  : "rgba(254, 226, 226, 0.9)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: isNormal ? "1px solid #059669" : "1px solid #dc2626",
                color: isNormal ? "#059669" : "#dc2626",
                px: 1,
                py: 0.25,
                borderRadius: "5px",
                fontSize: "10px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                whiteSpace: "nowrap",
                boxShadow: isNormal
                  ? "0 4px 12px rgba(5, 150, 105, 0.25)"
                  : "0 4px 12px rgba(220, 38, 38, 0.25)",
                letterSpacing: "0.2px",
              }}
            >
              {value}
              {unit}
            </Box>
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: isNormal ? "8px solid #059669" : "8px solid #dc2626",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Labels Row: min, normal range, max */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 0.25,
        }}
      >
        <Typography
          sx={{
            fontSize: "9px",
            color: "#9ca3af",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {min}
        </Typography>
        <Typography
          sx={{
            fontSize: "9px",
            color: "#10b981",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
          }}
        >
          Normal: {normalMin}-{normalMax}
        </Typography>
        <Typography
          sx={{
            fontSize: "9px",
            color: "#9ca3af",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {max}
        </Typography>
      </Box>
    </Box>
  );
};

// Static Personal report preview card, adapted from ReportComparisonSection (viewType === 'personal')
const PersonalReportPreview = () => {
  // Static example data
  const isNormal = true;
  const profile = {
    name: "Liver Health",
    description: "Focused view on the most important parameter for this patient.",
  };
  const parameter = {
    name: "ALT (SGPT)",
    description: "Your liver enzyme is within the healthy range. No immediate concern detected.",
    value: 32,
    min: 0,
    max: 120,
    normalMin: 10,
    normalMax: 45,
    unit: " U/L",
    tips: [
      "Maintain a balanced diet with plenty of fruits and vegetables.",
      "Limit alcohol and highly processed foods.",
      "Aim for at least 30 minutes of light exercise most days.",
      "Drink enough water throughout the day.",
      "Avoid unnecessary over‑the‑counter medications that may affect the liver.",
    ],
    recommendations: [
      "Repeat liver function test after 6–12 months as part of routine checkup.",
      "Discuss any long‑term medications with your doctor.",
      "Schedule an annual health check‑up to monitor liver health.",
      "Consider a follow‑up consultation if symptoms such as fatigue or jaundice appear.",
      "Keep a record of your lab results to track trends over time.",
    ],
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 1.5, sm: 2 },
        boxSizing: "border-box",
      }}
    >
      {/* Patient Details - matches SmartReportCard patient UI */}
      <Box
        sx={{
          mb: 1.5,
          p: "10px 12px",
          background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
          borderRadius: "10px",
          border: "1px solid #86efac",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "6px" }}>
          <PersonIcon sx={{ fontSize: "14px", color: "#10b981" }} />
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "10px",
              color: "#15803d",
              fontWeight: 600,
            }}
          >
            Patient Details
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "9px",
              color: "#1e293b",
              fontWeight: 500,
            }}
          >
            Name:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              John Doe
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "9px",
              color: "#1e293b",
              fontWeight: 500,
            }}
          >
            Age:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              35 years
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Profile Header */}
      <Box
        sx={{
          background: isNormal
            ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
          border: `2px solid ${isNormal ? "#86efac" : "#fcd34d"}`,
          borderRadius: "12px",
          p: { xs: 1.25, sm: 1.5 },
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: isNormal
                ? "linear-gradient(135deg, #10b981, #059669)"
                : "linear-gradient(135deg, #f59e0b, #d97706)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isNormal
                ? "0 4px 12px rgba(16, 185, 129, 0.3)"
                : "0 4px 12px rgba(245, 158, 11, 0.3)",
              flexShrink: 0,
            }}
          >
            <BiotechIcon sx={{ fontSize: 18, color: "#ecfdf5" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: { xs: "13px", sm: "14px" },
                fontWeight: 700,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {profile.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "10px",
                color: "#64748b",
                mt: 0.25,
                lineHeight: 1.4,
              }}
            >
              {profile.description}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Parameter Card */}
      <Box
        sx={{
          background: isNormal
            ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
          border: `2px solid ${isNormal ? "#86efac" : "#fcd34d"}`,
          borderRadius: "12px",
          p: { xs: 1.25, sm: 1.5 },
        }}
      >
        {/* Parameter Name and Status */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: "13px", sm: "14px" },
              fontWeight: 700,
              color: "#1e293b",
              flex: 1,
            }}
          >
            {parameter.name}
          </Typography>
          {isNormal ? (
            <CheckCircleIcon sx={{ fontSize: 16, color: "#10b981" }} />
          ) : (
            <WarningAmberIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
          )}
        </Box>

        {/* Parameter Description */}
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "11px",
            color: "#64748b",
            mb: 1,
            lineHeight: 1.5,
          }}
        >
          {parameter.description}
        </Typography>

        {/* Range Bar */}
        <Box sx={{ mb: 1 }}>
          <PreviewRangeBar
            value={parameter.value}
            min={parameter.min}
            max={parameter.max}
            normalMin={parameter.normalMin}
            normalMax={parameter.normalMax}
            unit={parameter.unit}
          />
        </Box>

        {/* Tips Section */}
        <Box
          sx={{
            mb: parameter.tips?.length ? 1 : 0,
            p: 0.75,
            background: "rgba(255, 255, 255, 0.7)",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <LightbulbIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "10px",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              Tips
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {parameter.tips.map((tip, idx) => (
              <Typography
                key={idx}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "9px",
                  color: "#475569",
                  lineHeight: 1.4,
                  pl: 1.25,
                  position: "relative",
                  "&::before": {
                    content: '"•"',
                    position: "absolute",
                    left: 0,
                    color: "#f59e0b",
                  },
                }}
              >
                {tip}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Recommendations Section */}
        <Box
          sx={{
            p: 0.75,
            background: "rgba(255, 255, 255, 0.7)",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <RecommendIcon sx={{ fontSize: 14, color: "#10b981" }} />
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "10px",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              Recommendations
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {parameter.recommendations.map((rec, idx) => (
              <Typography
                key={idx}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "9px",
                  color: "#475569",
                  lineHeight: 1.4,
                  pl: 1.5,
                  position: "relative",
                  "&::before": {
                    content: `"${idx + 1}."`,
                    position: "absolute",
                    left: 0,
                    color: "#10b981",
                    fontWeight: 600,
                  },
                }}
              >
                {rec}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PersonalReportPreview;


